import { Routes } from '@angular/router';
import { authGuard } from '@core/guards/auth.guard';
import { publicGuard } from '@core/guards/public.guard';
import { roleGuard } from '@core/guards/role.guard';
import { superAdminGuard } from '@core/guards/super-admin.guard';
import { featureGuard } from '@core/guards/feature.guard';

const ADMIN_ROLES = ['school_admin', 'principal'];
const MARKER_ROLES = ['teacher', 'school_admin', 'principal'];
const VISITOR_ROLES = ['school_admin', 'principal', 'receptionist'];
const FEE_DESK_ROLES = ['school_admin', 'accountant'];
const FEE_REPORT_ROLES = ['school_admin', 'accountant', 'principal'];
// Expenses — same role lists as the API (src/shared/constants/expenses.ts).
const EXPENSE_RECORDERS = ['school_admin', 'accountant', 'super_admin'];
const EXPENSE_APPROVERS = ['principal', 'vice_principal', 'super_admin'];
const EXPENSE_VIEWERS = ['school_admin', 'accountant', 'principal', 'vice_principal', 'super_admin'];
const SALARY_PAGE_ROLES = ['school_admin', 'accountant', 'principal', 'super_admin'];

export const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'dashboard' },

  // Public (auth) area — centered card layout.
  {
    path: '',
    loadComponent: () =>
      import('@layouts/auth-layout/auth-layout.component').then(
        (m) => m.AuthLayoutComponent,
      ),
    canActivate: [publicGuard],
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('@features/auth/login/login.component').then(
            (m) => m.LoginComponent,
          ),
      },
      {
        path: 'register',
        loadComponent: () =>
          import('@features/auth/register/register.component').then(
            (m) => m.RegisterComponent,
          ),
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import(
            '@features/auth/forgot-password/forgot-password.component'
          ).then((m) => m.ForgotPasswordComponent),
      },
    ],
  },

  // Public visitor area — centered card layout, NO guard (open to non-logged-in
  // visitors; a logged-in user isn't redirected away as with publicGuard).
  {
    path: 'visit',
    loadComponent: () =>
      import('@layouts/auth-layout/auth-layout.component').then(
        (m) => m.AuthLayoutComponent,
      ),
    children: [
      {
        path: 'book',
        loadComponent: () =>
          import(
            '@features/visitor/book-appointment/book-appointment.component'
          ).then((m) => m.BookAppointmentComponent),
      },
      {
        path: 'status',
        loadComponent: () =>
          import(
            '@features/visitor/check-status/check-status.component'
          ).then((m) => m.CheckStatusComponent),
      },
    ],
  },

  // Public admissions area — online application form + status check (no guard).
  {
    path: 'admissions',
    loadComponent: () =>
      import('@layouts/auth-layout/auth-layout.component').then(
        (m) => m.AuthLayoutComponent,
      ),
    children: [
      {
        path: 'apply',
        loadComponent: () =>
          import('@features/admissions/apply/apply.component').then(
            (m) => m.AdmissionApplyComponent,
          ),
      },
      {
        path: 'status',
        loadComponent: () =>
          import('@features/admissions/status/status.component').then(
            (m) => m.StatusComponent,
          ),
      },
    ],
  },

  // Platform (super-admin) console — separate layout + guard, on the global DB.
  {
    path: 'platform',
    loadComponent: () =>
      import('@layouts/platform-layout/platform-layout.component').then(
        (m) => m.PlatformLayoutComponent,
      ),
    canActivate: [superAdminGuard],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        path: 'dashboard',
        loadComponent: () =>
          import(
            '@features/platform/dashboard/platform-dashboard.component'
          ).then((m) => m.PlatformDashboardComponent),
      },
      {
        path: 'schools',
        loadComponent: () =>
          import('@features/platform/schools-list/schools-list.component').then(
            (m) => m.SchoolsListComponent,
          ),
      },
      {
        path: 'onboard',
        loadComponent: () =>
          import('@features/platform/onboard/onboard-wizard.component').then(
            (m) => m.OnboardWizardComponent,
          ),
      },
      {
        path: 'plans',
        loadComponent: () =>
          import('@features/platform/plans/plans-management.component').then(
            (m) => m.PlansManagementComponent,
          ),
      },
      {
        path: 'modules',
        loadComponent: () =>
          import('@features/platform/modules/modules-catalog.component').then(
            (m) => m.ModulesCatalogComponent,
          ),
      },
      {
        path: 'activity-log',
        loadComponent: () =>
          import(
            '@features/platform/activity-log/activity-log.component'
          ).then((m) => m.PlatformActivityLogComponent),
      },
      {
        path: 'schools/:id',
        loadComponent: () =>
          import(
            '@features/platform/school-detail/school-detail.component'
          ).then((m) => m.SchoolDetailComponent),
      },
    ],
  },

  // Authenticated area — sidebar + topbar layout.
  {
    path: '',
    loadComponent: () =>
      import('@layouts/main-layout/main-layout.component').then(
        (m) => m.MainLayoutComponent,
      ),
    canActivate: [authGuard],
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('@features/dashboard/dashboard.component').then(
            (m) => m.DashboardComponent,
          ),
      },
      {
        // A gated route whose module isn't in the school's plan lands here.
        path: 'upgrade',
        loadComponent: () =>
          import('@features/upgrade/upgrade.component').then(
            (m) => m.UpgradeComponent,
          ),
      },
      {
        path: 'students',
        loadComponent: () =>
          import('@features/students/student-list/student-list.component').then(
            (m) => m.StudentListComponent,
          ),
      },
      {
        path: 'students/new',
        canActivate: [roleGuard],
        data: { roles: ADMIN_ROLES },
        loadComponent: () =>
          import('@features/students/student-form/student-form.component').then(
            (m) => m.StudentFormComponent,
          ),
      },
      {
        path: 'students/:id',
        loadComponent: () =>
          import(
            '@features/students/student-detail/student-detail.component'
          ).then((m) => m.StudentDetailComponent),
      },
      {
        path: 'students/:id/edit',
        canActivate: [roleGuard],
        data: { roles: ADMIN_ROLES },
        loadComponent: () =>
          import('@features/students/student-form/student-form.component').then(
            (m) => m.StudentFormComponent,
          ),
      },
      {
        path: 'teachers',
        loadComponent: () =>
          import('@features/teachers/teacher-list/teacher-list.component').then(
            (m) => m.TeacherListComponent,
          ),
      },
      {
        path: 'teachers/new',
        canActivate: [roleGuard],
        data: { roles: ADMIN_ROLES },
        loadComponent: () =>
          import('@features/teachers/teacher-form/teacher-form.component').then(
            (m) => m.TeacherFormComponent,
          ),
      },
      {
        path: 'teachers/:id',
        loadComponent: () =>
          import(
            '@features/teachers/teacher-detail/teacher-detail.component'
          ).then((m) => m.TeacherDetailComponent),
      },
      {
        path: 'teachers/:id/edit',
        canActivate: [roleGuard],
        data: { roles: ADMIN_ROLES },
        loadComponent: () =>
          import('@features/teachers/teacher-form/teacher-form.component').then(
            (m) => m.TeacherFormComponent,
          ),
      },
      {
        path: 'attendance/mark',
        canActivate: [roleGuard],
        data: { roles: MARKER_ROLES },
        loadComponent: () =>
          import(
            '@features/attendance/mark-attendance/mark-attendance.component'
          ).then((m) => m.MarkAttendanceComponent),
      },
      {
        path: 'attendance/daily-report',
        canActivate: [roleGuard],
        data: { roles: ADMIN_ROLES },
        loadComponent: () =>
          import(
            '@features/attendance/daily-report/daily-report.component'
          ).then((m) => m.DailyReportComponent),
      },
      {
        path: 'attendance/class-summary',
        canActivate: [roleGuard],
        data: { roles: MARKER_ROLES },
        loadComponent: () =>
          import(
            '@features/attendance/class-summary/class-summary.component'
          ).then((m) => m.ClassSummaryComponent),
      },
      {
        path: 'attendance/holidays',
        canActivate: [roleGuard],
        data: { roles: ['school_admin'] },
        loadComponent: () =>
          import('@features/attendance/holidays/holidays.component').then(
            (m) => m.HolidaysComponent,
          ),
      },
      {
        path: 'notifications',
        loadComponent: () =>
          import(
            '@features/notifications/notification-list/notification-list.component'
          ).then((m) => m.NotificationListComponent),
      },
      {
        path: 'admin/send-notification',
        canActivate: [roleGuard],
        data: { roles: MARKER_ROLES },
        loadComponent: () =>
          import(
            '@features/notifications/send-notification/send-notification.component'
          ).then((m) => m.SendNotificationComponent),
      },
      {
        path: 'admin/notifications/sent',
        canActivate: [roleGuard],
        data: { roles: MARKER_ROLES },
        loadComponent: () =>
          import(
            '@features/notifications/sent-notifications/sent-notifications.component'
          ).then((m) => m.SentNotificationsComponent),
      },
      {
        path: 'admin/class-config',
        canActivate: [roleGuard],
        data: { roles: ADMIN_ROLES },
        loadComponent: () =>
          import(
            '@features/school-config/class-config/class-config.component'
          ).then((m) => m.ClassConfigComponent),
      },
      {
        path: 'admin/calendar',
        loadComponent: () =>
          import(
            '@features/calendar/school-calendar/school-calendar.component'
          ).then((m) => m.SchoolCalendarComponent),
      },
      {
        path: 'admin/timetable/config',
        canActivate: [roleGuard, featureGuard],
        data: { roles: ['school_admin'], feature: 'timetable' },
        loadComponent: () =>
          import(
            '@features/timetable/timetable-config/timetable-config.component'
          ).then((m) => m.TimetableConfigComponent),
      },
      {
        // Subject Hours Planner — admin/principal + timetable coordinators.
        // 'teacher' is allowed so a coordinator (a teacher) can reach it; the
        // API enforces the coordinator check (a non-coordinator teacher gets 403).
        path: 'admin/timetable/plan',
        canActivate: [roleGuard, featureGuard],
        data: { roles: ['school_admin', 'principal', 'teacher'], feature: 'timetable' },
        loadComponent: () =>
          import(
            '@features/timetable/subject-hours-plan/subject-hours-plan.component'
          ).then((m) => m.SubjectHoursPlanComponent),
      },
      {
        path: 'admin/timetable',
        canActivate: [featureGuard],
        data: { feature: 'timetable' },
        loadComponent: () =>
          import(
            '@features/timetable/timetable-view/timetable-view.component'
          ).then((m) => m.TimetableViewComponent),
      },
      {
        path: 'admin/substitutions',
        canActivate: [roleGuard, featureGuard],
        data: { roles: ADMIN_ROLES, feature: 'timetable' },
        loadComponent: () =>
          import(
            '@features/substitutions/substitutions.component'
          ).then((m) => m.SubstitutionsComponent),
      },
      {
        path: 'admin/exams',
        canActivate: [roleGuard, featureGuard],
        data: { roles: ADMIN_ROLES, feature: 'exams' },
        loadComponent: () =>
          import('@features/exams/exam-list/exam-list.component').then(
            (m) => m.ExamListComponent,
          ),
      },

      // ── Leave Management (S31) ──
      {
        path: 'leaves/my',
        loadComponent: () =>
          import('@features/leaves/my-leaves/my-leaves.component').then(
            (m) => m.MyLeavesComponent,
          ),
      },
      {
        path: 'leaves/approvals',
        canActivate: [roleGuard],
        data: { roles: ADMIN_ROLES },
        loadComponent: () =>
          import(
            '@features/leaves/leave-approvals/leave-approvals.component'
          ).then((m) => m.LeaveApprovalsComponent),
      },
      {
        path: 'admin/leaves/students',
        canActivate: [roleGuard],
        data: { roles: MARKER_ROLES },
        loadComponent: () =>
          import(
            '@features/leaves/student-leaves/student-leaves.component'
          ).then((m) => m.StudentLeavesComponent),
      },
      {
        path: 'admin/leaves/policy',
        canActivate: [roleGuard],
        data: { roles: ['school_admin'] },
        loadComponent: () =>
          import('@features/leaves/leave-policy/leave-policy.component').then(
            (m) => m.LeavePolicyComponent,
          ),
      },
      {
        path: 'admin/leaves',
        canActivate: [roleGuard],
        data: { roles: ADMIN_ROLES },
        loadComponent: () =>
          import('@features/leaves/team-leaves/team-leaves.component').then(
            (m) => m.TeamLeavesComponent,
          ),
      },

      // ── Fee Management (S34) ──
      {
        path: 'admin/fees/collect',
        canActivate: [roleGuard, featureGuard],
        data: { roles: FEE_DESK_ROLES, feature: 'fees' },
        loadComponent: () =>
          import('@features/fees/fee-collect/fee-collect.component').then(
            (m) => m.FeeCollectComponent,
          ),
      },
      {
        path: 'admin/fees/structure',
        canActivate: [roleGuard, featureGuard],
        data: { roles: ['school_admin'], feature: 'fees' },
        loadComponent: () =>
          import('@features/fees/fee-structure/fee-structure.component').then(
            (m) => m.FeeStructureComponent,
          ),
      },
      {
        path: 'admin/fees/reports',
        canActivate: [roleGuard, featureGuard],
        data: { roles: FEE_REPORT_ROLES, feature: 'fees' },
        loadComponent: () =>
          import('@features/fees/fee-reports/fee-reports.component').then(
            (m) => m.FeeReportsComponent,
          ),
      },
      {
        path: 'admin/fees/defaulters',
        canActivate: [roleGuard, featureGuard],
        data: { roles: FEE_REPORT_ROLES, feature: 'fees' },
        loadComponent: () =>
          import('@features/fees/fee-defaulters/fee-defaulters.component').then(
            (m) => m.FeeDefaultersComponent,
          ),
      },
      {
        path: 'admin/fees/settings',
        canActivate: [roleGuard, featureGuard],
        data: { roles: ['school_admin'], feature: 'fees' },
        loadComponent: () =>
          import('@features/fees/fee-settings/fee-settings.component').then(
            (m) => m.FeeSettingsComponent,
          ),
      },

      // ── Expenses & finance — specific paths BEFORE :id ──
      {
        path: 'admin/expenses',
        canActivate: [roleGuard, featureGuard],
        data: { roles: EXPENSE_VIEWERS, feature: 'finance' },
        loadComponent: () =>
          import('@features/expenses/expense-list/expense-list.component').then(
            (m) => m.ExpenseListComponent,
          ),
      },
      {
        path: 'admin/expenses/new',
        canActivate: [roleGuard, featureGuard],
        data: { roles: EXPENSE_RECORDERS, feature: 'finance' },
        loadComponent: () =>
          import('@features/expenses/expense-form/expense-form.component').then(
            (m) => m.ExpenseFormComponent,
          ),
      },
      {
        path: 'admin/expenses/approvals',
        canActivate: [roleGuard, featureGuard],
        data: { roles: EXPENSE_APPROVERS, feature: 'finance' },
        loadComponent: () =>
          import(
            '@features/expenses/expense-approvals/expense-approvals.component'
          ).then((m) => m.ExpenseApprovalsComponent),
      },
      {
        path: 'admin/expenses/salaries',
        canActivate: [roleGuard, featureGuard],
        data: { roles: SALARY_PAGE_ROLES, feature: 'finance' },
        loadComponent: () =>
          import('@features/expenses/salaries/salaries.component').then(
            (m) => m.SalariesComponent,
          ),
      },
      {
        // Recorders edit pending expenses; approvers may edit in any status.
        path: 'admin/expenses/:id/edit',
        canActivate: [roleGuard, featureGuard],
        data: { roles: [...EXPENSE_RECORDERS, ...EXPENSE_APPROVERS], feature: 'finance' },
        loadComponent: () =>
          import('@features/expenses/expense-form/expense-form.component').then(
            (m) => m.ExpenseFormComponent,
          ),
      },
      {
        path: 'admin/finance',
        canActivate: [roleGuard, featureGuard],
        data: { roles: EXPENSE_VIEWERS, feature: 'finance' },
        loadComponent: () =>
          import(
            '@features/expenses/finance-overview/finance-overview.component'
          ).then((m) => m.FinanceOverviewComponent),
      },

      // ── Admissions (admin) — specific paths BEFORE :id ──
      {
        path: 'admin/admissions',
        canActivate: [roleGuard, featureGuard],
        data: { roles: ADMIN_ROLES, feature: 'admissions' },
        loadComponent: () =>
          import(
            '@features/admissions/dashboard/admissions-dashboard.component'
          ).then((m) => m.AdmissionsDashboardComponent),
      },
      {
        path: 'admin/admissions/new',
        canActivate: [roleGuard, featureGuard],
        data: { roles: ADMIN_ROLES, feature: 'admissions' },
        loadComponent: () =>
          import('@features/admissions/new/admission-new.component').then(
            (m) => m.AdmissionNewComponent,
          ),
      },
      {
        path: 'admin/admissions/config',
        canActivate: [roleGuard, featureGuard],
        data: { roles: ['school_admin'], feature: 'admissions' },
        loadComponent: () =>
          import('@features/admissions/config/admission-config.component').then(
            (m) => m.AdmissionConfigComponent,
          ),
      },
      {
        path: 'admin/admissions/merit-list',
        canActivate: [roleGuard, featureGuard],
        data: { roles: ADMIN_ROLES, feature: 'admissions' },
        loadComponent: () =>
          import('@features/admissions/merit-list/merit-list.component').then(
            (m) => m.MeritListComponent,
          ),
      },
      {
        path: 'admin/admissions/:id',
        canActivate: [roleGuard, featureGuard],
        data: { roles: ADMIN_ROLES, feature: 'admissions' },
        loadComponent: () =>
          import('@features/admissions/detail/admission-detail.component').then(
            (m) => m.AdmissionDetailComponent,
          ),
      },
      {
        path: 'admin/visitors/history',
        canActivate: [roleGuard, featureGuard],
        data: { roles: ADMIN_ROLES, feature: 'visitors' },
        loadComponent: () =>
          import(
            '@features/visitors/visitor-history/visitor-history.component'
          ).then((m) => m.VisitorHistoryComponent),
      },
      {
        path: 'admin/visitors/gate-staff',
        canActivate: [roleGuard, featureGuard],
        data: { roles: ['school_admin'], feature: 'visitors' },
        loadComponent: () =>
          import('@features/visitors/gate-staff/gate-staff.component').then(
            (m) => m.GateStaffComponent,
          ),
      },
      {
        path: 'admin/visitors',
        canActivate: [roleGuard, featureGuard],
        data: { roles: VISITOR_ROLES, feature: 'visitors' },
        loadComponent: () =>
          import(
            '@features/visitors/visitor-dashboard/visitor-dashboard.component'
          ).then((m) => m.VisitorDashboardComponent),
      },
      {
        path: 'admin/marks-entry/:examId/progress',
        canActivate: [roleGuard, featureGuard],
        data: { roles: ADMIN_ROLES, feature: 'exams' },
        loadComponent: () =>
          import('@features/exams/marks-progress/marks-progress.component').then(
            (m) => m.MarksProgressComponent,
          ),
      },
      {
        path: 'admin/marks-entry/:examId',
        canActivate: [roleGuard, featureGuard],
        data: { roles: MARKER_ROLES, feature: 'exams' },
        loadComponent: () =>
          import('@features/exams/marks-entry/marks-entry.component').then(
            (m) => m.MarksEntryComponent,
          ),
      },
      {
        path: 'admin/results/:examId/analysis',
        canActivate: [roleGuard, featureGuard],
        data: { roles: MARKER_ROLES, feature: ['exams', 'reportCards'] },
        loadComponent: () =>
          import('@features/exams/subject-analysis/subject-analysis.component').then(
            (m) => m.SubjectAnalysisComponent,
          ),
      },
      {
        path: 'admin/results/:examId',
        canActivate: [roleGuard, featureGuard],
        data: { roles: MARKER_ROLES, feature: ['exams', 'reportCards'] },
        loadComponent: () =>
          import('@features/exams/class-results/class-results.component').then(
            (m) => m.ClassResultsComponent,
          ),
      },
    ],
  },

  {
    path: 'app-only',
    loadComponent: () =>
      import('@features/app-only/app-only.component').then(
        (m) => m.AppOnlyComponent,
      ),
  },

  {
    path: 'unauthorized',
    loadComponent: () =>
      import('@features/unauthorized/unauthorized.component').then(
        (m) => m.UnauthorizedComponent,
      ),
  },

  { path: '**', redirectTo: 'login' },
];
